import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { DomainsEntriTokenResponse } from '@customerio/hydra-ui-api';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { hydraUiApi } from '@/services/api';
import type { DomainsDomainResponse } from '@customerio/hydra-ui-api';
import { autoTlsDomainKeys, parseAutoTlsDomain, type AutoTlsDomain } from '../auto-tls-domain/entity';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { domainKeys, parseDomain, type Domain } from './entity';

/**
 * Domain reads. Same bulletproof shape as the rest of the cluster:
 * a plain async fetcher, a `getXQueryOptions` factory, and a thin hook
 * wrapping `useSuspenseQuery`.
 *
 * The list endpoint sideloads each domain into its own detail slot so
 * follow-up by-id reads (`useDomain({ id })`, when introduced) resolve
 * from cache without an extra GET.
 */

const STALE_TIME = 60_000;

/**
 * Resolves to the URL's workspace id when it's a positive integer; otherwise
 * null. **Reactive** — wraps the router `useLocation`-backed hook so
 * persistent components like `delivery-mode-banner` re-render on workspace
 * switch. Don't replace with the non-hook `getWorkspaceIdFromCurrentUrl()`
 * from `@/utils/workspace-id`; that reads `window.location.href` directly
 * and won't trigger re-renders, so the banner would keep rendering the
 * previous workspace's domains until something else forced a re-render.
 */
function useValidWorkspaceId(): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

/**
 * The list read's two collections. Both keep the endpoint's own field names: the
 * wrapper is here for the parsing and detail-slot seeding `getDomains` does, not
 * to rename anything.
 */
export interface DomainsList {
  domains: readonly Domain[];
  /**
   * The auto-TLS domains the list endpoint sideloads, keyed by
   * `domain.auto_tls_domain_id`. A domain's link-tracking state lives here
   * (provisioning / certificate failure), not on the domain row, so any screen
   * showing that state reads the pair together rather than issuing a second
   * request for the auto-TLS collection.
   */
  auto_tls_domains: readonly AutoTlsDomain[];
}

/** @public */
export async function getDomains(workspaceId: number, qc: QueryClient): Promise<DomainsList> {
  const data = await hydraUiApi.domains.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (data.domains ?? []).map(parseDomain);
  for (const d of parsed) {
    qc.setQueryData(domainKeys.detail(workspaceId, d.id), d);
  }
  return { domains: parsed, auto_tls_domains: data.auto_tls_domains ?? [] };
}

/** @public */
export const getDomainsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    // Explicit `undefined` — `TListParams = void` for the domain entity,
    // so this is the no-filters list slot.
    queryKey: domainKeys.list(workspaceId, undefined),
    queryFn: () => getDomains(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseDomainsOptions {
  queryConfig?: QueryConfig<typeof getDomainsQueryOptions>;
}

/** @public */
export const useDomains = ({ queryConfig }: UseDomainsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getDomainsQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};

/**
 * Non-suspending domains read for callers that mount before the URL
 * settles (workspace-less layouts, root-of-app banners). Returns
 * `data: undefined` while no workspace id is in scope or the fetch is
 * pending; once the fetch lands, `data: { domains: [...] }` (which
 * may itself be empty).
 *
 * Always access via `result.data?.domains` (or destructure with a
 * default) — distinguishing the "loading / no workspace" state from
 * the "loaded but empty" state matters whenever a UX cares about the
 * difference (e.g. banner copy that should hide during loading).
 *
 *   const { data } = useDomainsOptional();
 *   const verifiedDomains = data?.domains.filter((d) => d.verified === true) ?? null;
 *
 *   // Three states the consumer must distinguish:
 *   //   data === undefined        → loading or no workspace yet
 *   //   data?.domains.length === 0 → loaded, workspace has no domains
 *   //   data?.domains.length > 0   → loaded, has domains
 *   if (verifiedDomains == null) return null;          // hide while loading
 *   if (verifiedDomains.length === 0) return <Empty/>; // explicit empty state
 *   return <DomainsList domains={verifiedDomains} />;
 */
interface UseDomainsOptionalOptions<TSelected> {
  /** Use the resolved workspace when the current URL is workspace-less (for example settings). */
  workspaceIdOverride?: number;
  /**
   * Memoized projection over the loaded `DomainsList`. Returning a
   * structurally-shared subtree (TanStack Query's `select` does the
   * shallow comparison) keeps the result reference stable across cache
   * updates that don't change the projected value — avoids the
   * caller-side `useMemo` dance for filtered views.
   */
  select?: (data: DomainsList) => TSelected;
  // Typed against the raw `DomainsList` (not `TSelected`): the refetch-interval
  // callback inspects `query.state.data`, which is always the fetched data,
  // and pinning it here keeps the callback well-typed when no `select` is given.
  refetchInterval?: UseQueryOptions<DomainsList, Error, DomainsList, QueryKey>['refetchInterval'];
  /**
   * Gate the fetch independently of the workspace id (defaults to enabled).
   * Distinct from the `skipToken` no-workspace guard below: a caller that
   * mounts the step before it should fetch (an inactive onboarding step)
   * passes `false` here without losing the workspace-id routing.
   */
  enabled?: boolean;
}

/** @public */
export function useDomainsOptional(): ReturnType<typeof useQuery<DomainsList>>;
export function useDomainsOptional<TSelected>(
  options: UseDomainsOptionalOptions<TSelected>,
): ReturnType<typeof useQuery<DomainsList, Error, TSelected>>;
export function useDomainsOptional<TSelected = DomainsList>(options?: UseDomainsOptionalOptions<TSelected>) {
  const urlWorkspaceId = useValidWorkspaceId();
  const validWorkspaceId =
    options?.workspaceIdOverride != null && options.workspaceIdOverride > 0
      ? options.workspaceIdOverride
      : urlWorkspaceId;
  const qc = useQueryClient();
  return useQuery({
    // When the workspace id isn't in scope yet, route the query to a
    // sentinel key with `skipToken` for the queryFn — TanStack Query
    // skips the fetch and never registers a `domainKeys.list(0)` slot
    // in the real workspace namespace.
    //
    // The sentinel key sits outside the `['workspace', wsId, ...]`
    // namespace so it survives `clearWorkspaceQueries`, but `skipToken`
    // guarantees no data ever lands here — there is nothing to evict.
    // Don't `setQueryData` against this key; treat it as write-frozen.
    //
    // `skipToken` is load-bearing here, NOT just a stylistic substitute
    // for `enabled: false`. The render where `validWorkspaceId` is null
    // captures a stale `qc` reference; once the workspace resolves, we
    // re-render and recompute both the queryFn closure and the queryKey,
    // so the new fetch sees the current `qc`. Don't drop `skipToken`
    // without also adding `enabled: validWorkspaceId != null`.
    queryKey: validWorkspaceId != null ? domainKeys.list(validWorkspaceId, undefined) : ['domain', 'no-workspace'],
    queryFn: validWorkspaceId != null ? () => getDomains(validWorkspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
    refetchInterval: options?.refetchInterval,
    select: options?.select,
    enabled: options?.enabled,
  });
}

async function fetchDomain(workspaceId: number, domainId: string): Promise<DomainsDomainResponse> {
  return hydraUiApi.domains.get({
    params: { path: { environment_id: String(workspaceId), domain_id: domainId } },
  });
}

/**
 * Imperative single-domain read. Used inside callbacks (DNS-setup
 * orchestration) rather than as a hook, mirroring the previous
 * `api.getDomainById` call. Returns the typed domain row.
 *
 * @public
 */
export async function getDomainById(workspaceId: number, domainId: string): Promise<Domain> {
  const response = await fetchDomain(workspaceId, domainId);
  if (response.domain == null) {
    throw new Error('getDomainById: response did not include a domain');
  }
  return response.domain;
}

/**
 * Cached single-domain read, sharing the detail slot the list sideloads into.
 *
 * The response also carries the domain's auto-TLS row — where link tracking's
 * certificate state lives — so it is written straight into the auto-TLS entity's
 * own detail slot when the read is still current. A screen showing that state
 * then reads it from there and can poll it on its own cadence without re-reading
 * the domain.
 *
 * @public
 */
export async function getDomain(
  workspaceId: number,
  domainId: string,
  qc: QueryClient,
  canUpdateCache: () => boolean,
): Promise<Domain> {
  const response = await fetchDomain(workspaceId, domainId);
  if (response.domain == null) {
    throw new Error('getDomain: response did not include a domain');
  }
  if (response.auto_tls_domain != null && canUpdateCache()) {
    const autoTlsDomain = parseAutoTlsDomain(response.auto_tls_domain);
    qc.setQueryData(autoTlsDomainKeys.detail(workspaceId, String(autoTlsDomain.id)), autoTlsDomain);
  }
  return parseDomain(response.domain);
}

/** @public */
export const getDomainQueryOptions = (workspaceId: number, domainId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: domainKeys.detail(workspaceId, domainId),
    queryFn: ({ signal }) => getDomain(workspaceId, domainId, qc, () => !signal.aborted),
    staleTime: STALE_TIME,
  });

interface UseDomainOptions {
  domainId: string;
  queryConfig?: QueryConfig<typeof getDomainQueryOptions>;
}

/** @public */
export const useDomain = ({ domainId, queryConfig }: UseDomainOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getDomainQueryOptions(workspaceId, domainId, qc),
    ...queryConfig,
  });
};

/** Which set of records the Entri session should publish. */
export type EntriTokenFlow = 'domain_auth' | 'link_tracking';

/**
 * Imperative Entri-token read for DNS setup. The backend defaults an omitted or
 * unknown flow to `domain_auth` (`normalizeEntriFlow`), so only the
 * link-tracking flow actually depends on the query param.
 *
 * @public
 */
export async function getDomainEntriToken(
  workspaceId: number,
  domainId: string,
  flow: EntriTokenFlow = 'domain_auth',
): Promise<DomainsEntriTokenResponse> {
  return hydraUiApi.domains.getEntriToken({
    params: { path: { environment_id: String(workspaceId), domain_id: domainId }, query: { flow } },
  });
}
